import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const LS = "bq_cart";
const LS_VER = "bq_cart_v";
const CURRENT_VER = "2"; // bump when we need to force-clear polluted client carts
const MAX_QTY = 99; // defensive cap so a bad merge can never balloon
const clampQty = (n) => Math.max(1, Math.min(MAX_QTY, Number(n) || 1));
const sanitize = (arr) =>
  (Array.isArray(arr) ? arr : []).map((i) => ({ ...i, quantity: clampQty(i.quantity) }));
const readLS = () => {
  try {
    // one-shot migration: wipe any cart from the doubling-bug era
    if (typeof window !== "undefined" && localStorage.getItem(LS_VER) !== CURRENT_VER) {
      localStorage.removeItem(LS);
      localStorage.setItem(LS_VER, CURRENT_VER);
      return [];
    }
    return sanitize(JSON.parse(localStorage.getItem(LS) || "[]"));
  } catch { return []; }
};
const writeLS = (v) => localStorage.setItem(LS, JSON.stringify(v));

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(readLS());
  const [open, setOpen] = useState(false);
  // Track which user id we've already synced against so mount/refresh doesn't re-merge
  const hydratedFor = useRef(null);

  // sync from backend on login — ONCE per user session
  useEffect(() => {
    if (!user) { hydratedFor.current = null; return; }
    if (hydratedFor.current === user.id) return;
    hydratedFor.current = user.id;
    (async () => {
      try {
        const { data } = await api.get("/cart");
        const serverItems = sanitize(data?.items || []);
        const local = readLS();
        // Only merge (union of anonymous local items into server) when both sides have content.
        // If server is empty, adopt local. If local is empty, adopt server.
        let next;
        if (serverItems.length === 0) next = local;
        else if (local.length === 0) next = serverItems;
        else next = mergeCarts(serverItems, local);
        setItems(next);
        writeLS(next);
        // Only push back to server if we actually changed it
        if (JSON.stringify(next) !== JSON.stringify(serverItems)) {
          await api.put("/cart", next);
        }
      } catch {}
    })();
  }, [user]);

  const persist = async (next) => {
    const safe = sanitize(next);
    setItems(safe);
    writeLS(safe);
    if (user) { try { await api.put("/cart", safe); } catch {} }
  };

  const add = (product, size = "", color = "", qty = 1) => {
    const key = `${product.id}|${size}|${color}`;
    const found = items.find(i => `${i.product_id}|${i.size}|${i.color}` === key);
    let next;
    if (found) next = items.map(i => i === found ? { ...i, quantity: clampQty(i.quantity + qty) } : i);
    else next = [...items, {
      product_id: product.id, title: product.title, subtitle: product.subtitle || "",
      price: product.price, image: product.images?.[0] || product.image, size, color, quantity: clampQty(qty),
    }];
    persist(next);
    toast.success("Added to cart", { description: product.title });
    setOpen(true);
  };

  const update = (idx, qty) => {
    const next = items.map((i, k) => k === idx ? { ...i, quantity: clampQty(qty) } : i);
    persist(next);
  };

  const remove = (idx) => persist(items.filter((_, k) => k !== idx));
  const clear = () => persist([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ items, add, update, remove, clear, subtotal, count, open, setOpen }}>
      {children}
    </CartCtx.Provider>
  );
};

function mergeCarts(a, b) {
  const map = new Map();
  [...a, ...b].forEach(i => {
    const k = `${i.product_id}|${i.size}|${i.color}`;
    const cur = map.get(k);
    if (cur) map.set(k, { ...cur, quantity: clampQty(cur.quantity + i.quantity) });
    else map.set(k, { ...i, quantity: clampQty(i.quantity) });
  });
  return Array.from(map.values());
}
